import {
	CreateDrawerFormProps,
	DateTimeField,
	FormDrawer,
	FormDrawerContent,
	FormDrawerContentProps,
	FormFetchDrawer,
	IShowEndpoint,
	TextField,
	TextareaField,
	UpdateDrawerFormProps,
	isExternalUrl,
} from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { CompanySelectField } from "../companies/CompanySelectField";
import { ContactSelectField } from "../shared/select/ContactSelectField";
import { EventSingleSelect } from "../shared/select/EventSelect";
import { IClippingDetailResource, IClippingFormValues } from "../shared/types";

const useValidate = () => {
	const { t } = useTranslation();

	return (formValues: IClippingFormValues) => {
		const errors: { [K in keyof IClippingFormValues]?: any } = {};

		if (!formValues.title) {
			errors.title = t("Required field");
		}
		if (formValues.link && !isExternalUrl(formValues.link)) {
			errors.link = t("The URL must be a valid external URL.");
		}
		return errors;
	};
};

interface ClippingFormDrawerContentProps {
	isCompanyReadOnly?: boolean;
	isContactReadOnly?: boolean;
}

const ClippingFormDrawerContent: FC<FormDrawerContentProps & ClippingFormDrawerContentProps> = (props) => {
	const { t } = useTranslation();
	const { isContactReadOnly, isCompanyReadOnly } = props;

	return (
		<FormDrawerContent {...props}>
			<TextField name="title" label={t("Title")} required />
			<TextField name="link" label={t("URL")} />
			<TextareaField name="body" label={t("Content")} />
			<DateTimeField name="publishedAt" label={t("Published at")} />
			<ContactSelectField label={t("Contact")} name="contact" readOnly={isContactReadOnly} />
			<EventSingleSelect label={t("Event")} name="event" />
			<CompanySelectField label={t("Company")} name="company" readOnly={isCompanyReadOnly} />
			<TextField name="reach" label={t("Reach")} />
			<TextField name="mediaEquivalenceValue" label={t("Media equivalence value")} />
		</FormDrawerContent>
	);
};

interface ClippingCreateDrawerProps extends ClippingFormDrawerContentProps {
	emptyClipping: IClippingFormValues;
}

export const ClippingCreateDrawer: FC<
	CreateDrawerFormProps<IClippingFormValues> & ClippingCreateDrawerProps & ClippingFormDrawerContentProps
> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<FormDrawer
			{...props}
			initialValues={props.emptyClipping}
			title={t("Create clipping")}
			validate={validate}
			renderForm={(formProps) => <ClippingFormDrawerContent {...formProps} {...props} />}
		/>
	);
};

interface ClippingUpdateDrawerProps
	extends UpdateDrawerFormProps<IClippingDetailResource>,
		Omit<ClippingFormDrawerContentProps, "provider"> {
	service: IShowEndpoint<IClippingDetailResource>;
}

export const ClippingUpdateDrawer: FC<ClippingUpdateDrawerProps> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<FormFetchDrawer
			{...props}
			title={t("Edit clipping")}
			queryFn={() => props.service.show(props.id).then((x) => x.data.data)}
			queryKey={props.service.showKey(props.id)}
			validate={validate}
			renderSuccess={(formProps) => <ClippingFormDrawerContent {...props} {...formProps} />}
		/>
	);
};
